import * as React from "react";
import { Marker } from "react-map-gl/maplibre";
import { Geo } from "../messages";
import BaseMap from "./BaseMap";

export interface MapWithLocationsProps {
  location: Geo[];
  zoom: number;
}

/**
 * This must only be instantiated after google_maps_api has been loaded
 */
export const MapWithLocations = (props: MapWithLocationsProps) => {
  const center = averageGeolocation(props.location);

  return (
    <BaseMap destination={center} zoom={15}>
      <MapMarker position={center} />
    </BaseMap>
  );
};

const MapMarker = (
  props: React.PropsWithChildren<{
    position: Geo;
  }>
) => {
  return (
    <Marker latitude={props.position.lat} longitude={props.position.lng}>
      <img
        src="https://s3.amazonaws.com/medflyt-mobile-assets/map_available_visit_destination.png"
        width={50}
        height={59}
      />
    </Marker>
  );
};

/**
 * Calculate the center/average of multiple GeoLocation coordinates
 * Expects an array of objects with .latitude and .longitude properties
 *
 * @url http://stackoverflow.com/a/14231286/538646
 */
function averageGeolocation(coords: Geo[]): Geo {
  if (coords.length === 1) {
    return coords[0];
  }

  let x = 0;
  let y = 0;
  let z = 0;

  for (const coord of coords) {
    const latitude = (coord.lat * Math.PI) / 180;
    const longitude = (coord.lng * Math.PI) / 180;

    x += Math.cos(latitude) * Math.cos(longitude);
    y += Math.cos(latitude) * Math.sin(longitude);
    z += Math.sin(latitude);
  }

  const total = coords.length;

  x = x / total;
  y = y / total;
  z = z / total;

  const centralLongitude = Math.atan2(y, x);
  const centralSquareRoot = Math.sqrt(x * x + y * y);
  const centralLatitude = Math.atan2(z, centralSquareRoot);

  return {
    lat: (centralLatitude * 180) / Math.PI,
    lng: (centralLongitude * 180) / Math.PI,
  };
}
